/**
 * Sign-in screen (FE1 — login + shell). Plain email/password form against
 * Amplify (admin-provisioned accounts; no self-signup). Deliberately minimal,
 * disposable styling — this whole frontend is the "show Ted + Ben" version, to
 * be replaced by Ben's real design. `humanise` maps Cognito errors to friendly
 * text (kept from bento).
 */

import { useState } from "react";
import type { CSSProperties } from "react";
import { useAuth } from "../../contexts/AuthContext.js";

export default function SignIn() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signIn(email.trim().toLowerCase(), password);
      // On success the AuthProvider flips the gate to the app; nothing to do.
    } catch (err) {
      setError(humanise(err));
      setPending(false);
    }
  }

  return (
    <div style={styles.wrap}>
      <form style={styles.card} onSubmit={onSubmit}>
        <h1 style={styles.title}>Catalyst</h1>
        <p style={styles.subtitle}>Sign in to explore the dashboard.</p>
        {error && <div style={styles.error}>{error}</div>}
        <label style={styles.label}>
          Email
          <input
            style={styles.input}
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label style={styles.label}>
          Password
          <div style={styles.passwordWrap}>
            <input
              style={{ ...styles.input, paddingRight: "44px", width: "100%", boxSizing: "border-box" }}
              type={showPassword ? "text" : "password"}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {/* Show/hide toggle — helps catch a mistyped password on login. */}
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={styles.eye}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </label>
        <button style={styles.button} type="submit" disabled={pending}>
          {pending ? "Signing in…" : "Sign in"}
        </button>
        <p style={styles.footer}>Access is provisioned by your administrator.</p>
      </form>
    </div>
  );
}

// Password show/hide icons — inline SVG (Feather-style eye / eye-off, MIT), so
// there's no external asset request and they take the button's currentColor.
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/** Map a Cognito/Amplify sign-in error to a friendly message. Exported so it
 *  can be unit-tested without the DOM if we add tests later. */
export function humanise(err: unknown): string {
  const name = (err as { name?: string })?.name ?? "";
  if (name === "NotAuthorizedException") return "Wrong email or password.";
  if (name === "UserNotConfirmedException")
    return "This account isn't active yet — contact your administrator.";
  if (name === "UserNotFoundException") return "No account found for this email.";
  if (name === "PasswordResetRequiredException")
    return "Your password needs resetting — contact your administrator.";
  return (err as { message?: string })?.message ?? "Sign-in failed. Please try again.";
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    minHeight: "100vh",
    background: "#0b1220",
    display: "grid",
    placeItems: "center",
    padding: "24px",
    fontFamily: "system-ui, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: "360px",
    background: "#131c2e",
    borderRadius: "12px",
    padding: "28px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
  },
  title: { color: "#e8edf6", margin: 0, fontSize: "24px" },
  subtitle: { color: "#8ba0c0", margin: "0 0 8px", fontSize: "14px" },
  label: {
    color: "#b7c6de",
    fontSize: "13px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  input: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #2a3854",
    background: "#0b1220",
    color: "#e8edf6",
    fontSize: "14px",
  },
  passwordWrap: { position: "relative", display: "flex", alignItems: "center" },
  eye: {
    position: "absolute",
    right: "8px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#8ba0c0",
    lineHeight: 0,
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
  button: {
    marginTop: "8px",
    padding: "11px",
    borderRadius: "8px",
    border: "none",
    background: "#3b82f6",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
  },
  error: {
    background: "#3a1720",
    color: "#ffb4c0",
    padding: "10px 12px",
    borderRadius: "8px",
    fontSize: "13px",
  },
  footer: { color: "#6b7f9e", fontSize: "12px", margin: "4px 0 0", textAlign: "center" },
};
